import { useEffect, useRef, useState, Suspense } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import { getUser } from "redux-store/auth/actions";
import { hasStoredAuthToken } from "utils/authToken";
import { loadCapabilities } from "redux-store/abac/actions";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import EnergyPortalShell from "energy/components/EnergyPortalShell";
import SwitchSystemModal from "components/SwitchSystemModal";

import theme from "assets/theme";

import routes from "routes";

import { selectUserDetails } from "config/appMode";

// energy-webapp is a single-system app: authenticated pages always render
// inside the EnergyPortalShell (the orange-rail sidebar). There is no
// Sidenav/Configurator and no RTL cache — those belong to the
// multi-vertical flex2-webapp this repo was cloned from.
//
// It does carry a switch-system modal, though: the sidebar's "Energy" badge
// only renders as a button when a handler is passed down, so without this
// the badge was inert and there was no way out of the Energy lane.
export default function App() {
  const { pathname } = useLocation();

  // selectUserDetails normalizes the two coexisting state.auth.user shapes
  // (post-login is the inner DTO; post-getUser is the outer wrapper).
  const reduxDispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const userDetails = selectUserDetails(authUser);

  // Rehydrate state.auth.user on hard refresh. Without this, the Redux
  // store starts empty on every reload and the shell never mounts even
  // though the user is still authenticated (their authToken sits in
  // localStorage). getUser is idempotent; if the token is bad,
  // getUserFailure clears it and the catch-all bounces to /login.
  useEffect(() => {
    // hasStoredAuthToken() rejects leftover placeholders ("", "undefined",
    // "null") so we never fire an unauthenticated POST /auth/user-info.
    if (!userDetails && hasStoredAuthToken()) {
      reduxDispatch(getUser());
    }
  }, [userDetails, reduxDispatch]);

  // ── ABAC capability bootstrap ──────────────────────────────────────────
  // Once the user is known, load their capability map exactly once. This
  // populates state.abac.capabilities, which EnergyPortalNavigation and the
  // permission hooks read. The ref guards against StrictMode/HMR
  // double-invocation; it resets when the user clears (logout) so a
  // subsequent login re-fetches.
  const abacCapabilities = useSelector((state) => state.abac?.capabilities);
  const abacCapabilitiesLoading = useSelector((state) => state.abac?.loading?.capabilities);
  // Switch-system modal, driven by the sidebar's "Energy" badge.
  const [switchSystemOpen, setSwitchSystemOpen] = useState(false);

  const capsRequestedRef = useRef(false);
  useEffect(() => {
    if (!userDetails) {
      capsRequestedRef.current = false;
      return;
    }
    if (!abacCapabilities && !abacCapabilitiesLoading && !capsRequestedRef.current) {
      capsRequestedRef.current = true;
      reduxDispatch(loadCapabilities());
    }
  }, [userDetails, abacCapabilities, abacCapabilitiesLoading, reduxDispatch]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  const routesBlock = (
    <Suspense fallback={<div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>}>
      <Routes>
        {getRoutes(routes)}
        {/* Catch-all ALWAYS goes to /login. localStorage.authToken can lie
            (stale tokens from a logged-out session); Login.js's own
            already-authed effect fires the proper post-login redirect when
            the token is still valid. Same rationale as flex2-webapp. */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );

  // Auth-flow paths ALWAYS render bare — even when redux still has stale
  // userDetails from a previous session, a logged-out user shouldn't see
  // the portal chrome on /login.
  const isAuthPath = /^\/(login|forgot-password|reset-password|sign-up|auth-check|switch-org|verify|two-factor)(\/|$)/.test(
    pathname
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {isAuthPath || !userDetails ? (
        routesBlock
      ) : (
        <>
          <EnergyPortalShell onSwitchSystem={() => setSwitchSystemOpen(true)}>
            {routesBlock}
          </EnergyPortalShell>
          <SwitchSystemModal
            open={switchSystemOpen}
            onClose={() => setSwitchSystemOpen(false)}
          />
        </>
      )}
    </ThemeProvider>
  );
}
